import React from "react";

export function PageHeader({ title, subtitle, actions, testid }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6" data-testid={testid}>
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
          {title}
        </h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, hint, accent = "navy", testid }) {
  const accents = {
    navy: "bg-[#0A2540] text-white",
    gold: "bg-[#FBF4DF] text-[#0A2540] border border-[#F1E3B5]",
    emerald: "bg-emerald-50 text-emerald-900 border border-emerald-200",
    rose: "bg-rose-50 text-rose-900 border border-rose-200",
  }[accent];
  return (
    <div className={`rounded-2xl p-5 ${accents}`} data-testid={testid}>
      <p className="text-[11px] uppercase tracking-widest opacity-70 font-bold">{label}</p>
      <p className="mt-2 text-3xl font-extrabold" style={{ fontFamily: "Poppins" }}>{value}</p>
      {hint && <p className="mt-1 text-xs opacity-80">{hint}</p>}
    </div>
  );
}

export function DataTable({ columns, rows, empty = "No data", testid }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400 text-sm" data-testid={`${testid}-empty`}>
        {empty}
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="text-left font-semibold px-4 py-3 whitespace-nowrap text-xs uppercase tracking-wider">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row.id || row.user_id || idx} className="border-t border-slate-100 hover:bg-slate-50/50">
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-3 align-middle">
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Pagination({ total, skip, limit, onChange, testid }) {
  const start = total === 0 ? 0 : skip + 1;
  const end = Math.min(skip + limit, total);
  const canPrev = skip > 0;
  const canNext = end < total;
  return (
    <div className="flex items-center justify-between mt-3 text-xs text-slate-500" data-testid={testid}>
      <span>{start}–{end} of {total}</span>
      <div className="flex gap-2">
        <button
          disabled={!canPrev}
          onClick={() => onChange(Math.max(0, skip - limit))}
          className="px-3 py-1.5 rounded-md border border-slate-200 disabled:opacity-40 hover:border-[#0A2540]"
          data-testid={`${testid}-prev`}
        >Prev</button>
        <button
          disabled={!canNext}
          onClick={() => onChange(skip + limit)}
          className="px-3 py-1.5 rounded-md border border-slate-200 disabled:opacity-40 hover:border-[#0A2540]"
          data-testid={`${testid}-next`}
        >Next</button>
      </div>
    </div>
  );
}

export function Pill({ children, color = "slate" }) {
  const map = {
    slate: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-100 text-emerald-800",
    amber: "bg-amber-100 text-amber-800",
    rose: "bg-rose-100 text-rose-800",
    blue: "bg-blue-100 text-blue-800",
    gold: "bg-[#FBF4DF] text-[#0A2540] border border-[#F1E3B5]",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${map[color] || map.slate}`}>
      {children}
    </span>
  );
}

export function FilterBar({ children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-3 flex flex-wrap gap-2 mb-4" data-testid={testid}>
      {children}
    </div>
  );
}
